import { useState } from 'react'
import type { ForecastRun, UserPrediction } from '../types'
import './ProbabilityChart.css'

interface ProbabilityChartProps {
  runs: ForecastRun[]
  userPredictions?: UserPrediction[]
  height?: number
}

export default function ProbabilityChart({
  runs,
  userPredictions = [],
  height = 280,
}: ProbabilityChartProps) {
  // Filter to only completed runs with final results, sorted by creation time
  const completedRuns = runs
    .filter((r) => r.final_result)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())

  // Track current prediction index
  const [currentIndex, setCurrentIndex] = useState(0)

  // Track whether showing user predictions
  const [showingUserPredictions, setShowingUserPredictions] = useState(false)
  const [currentUserIndex, setCurrentUserIndex] = useState(0)

  // Track whether showing timeline view
  const [showTimelineView, setShowTimelineView] = useState(false)

  if (completedRuns.length === 0) {
    return (
      <div className="probability-chart-empty" style={{ height: `${height}px` }}>
        <div className="chart-empty-message">
          No completed predictions to display
        </div>
      </div>
    )
  }

  const currentRun = completedRuns[currentIndex]
  const totalPredictions = completedRuns.length
  const totalUserPredictions = userPredictions.length

  // Get current data to display
  let displayData: {
    finalProbability: number | null
    agentProbabilities: number[]
    userPrediction?: UserPrediction
    title: string
    subtitle: string
  }

  if (showingUserPredictions && userPredictions.length > 0) {
    const userPred = userPredictions[currentUserIndex]
    displayData = {
      finalProbability: null,
      agentProbabilities: [],
      userPrediction: userPred,
      title: `User prediction ${currentUserIndex + 1}`,
      subtitle: userPred.user_name,
    }
  } else {
    const agentProbs = (currentRun.agent_predictions || [])
      .filter((a) => a.probability !== null && a.probability !== undefined)
      .map((a) => a.probability! * 100)
    
    displayData = {
      finalProbability: currentRun.final_result?.probability ? currentRun.final_result.probability * 100 : null,
      agentProbabilities: agentProbs,
      userPrediction: undefined,
      title: `Prediction ${currentIndex + 1}`,
      subtitle: new Date(currentRun.created_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    }
  }

  const handlePrevious = () => {
    if (showingUserPredictions) {
      if (currentUserIndex > 0) {
        setCurrentUserIndex(currentUserIndex - 1)
      } else {
        // Switch back to AI predictions
        setShowingUserPredictions(false)
        setCurrentIndex(totalPredictions - 1)
      }
    } else {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1)
      }
    }
  }

  const handleNext = () => {
    if (showingUserPredictions) {
      if (currentUserIndex < totalUserPredictions - 1) {
        setCurrentUserIndex(currentUserIndex + 1)
      }
    } else {
      if (currentIndex < totalPredictions - 1) {
        setCurrentIndex(currentIndex + 1)
      } else if (totalUserPredictions > 0) {
        // Switch to user predictions
        setShowingUserPredictions(true)
        setCurrentUserIndex(0)
      }
    }
  }

  const canGoPrevious = showingUserPredictions ? (currentUserIndex > 0 || totalPredictions > 0) : currentIndex > 0
  const canGoNext = showingUserPredictions 
    ? currentUserIndex < totalUserPredictions - 1 
    : currentIndex < totalPredictions - 1 || totalUserPredictions > 0

  return (
    <div className="probability-chart-modern">
      {/* Header with navigation */}
      <div className="chart-header">
        {showTimelineView ? (
          <>
            <div className="chart-title-group">
              <div className="chart-title">Timeline View</div>
              <div className="chart-subtitle">All predictions over time</div>
            </div>
            <button
              type="button"
              className="chart-timeline-toggle"
              onClick={() => setShowTimelineView(false)}
            >
              Show Individual
            </button>
          </>
        ) : (
          <>
            <div className="chart-title-group">
              <div className="chart-title">{displayData.title}</div>
              <div className="chart-subtitle">{displayData.subtitle}</div>
            </div>
            <div className="chart-nav-wrapper">
              {totalPredictions > 1 && !showingUserPredictions && (
                <button
                  type="button"
                  className="chart-timeline-toggle"
                  onClick={() => setShowTimelineView(true)}
                >
                  Plot All
                </button>
              )}
              {(totalPredictions + totalUserPredictions > 1) && (
                <>
                  {/* Visual prediction indicators */}
                  <div className="chart-prediction-indicators">
                    {Array.from({ length: totalPredictions }).map((_, i) => (
                      <div
                        key={`ai-${i}`}
                        className={`prediction-dot prediction-dot-ai ${!showingUserPredictions && i === currentIndex ? 'active' : ''}`}
                        title={`AI Prediction ${i + 1}`}
                      />
                    ))}
                    {Array.from({ length: totalUserPredictions }).map((_, i) => (
                      <div
                        key={`user-${i}`}
                        className={`prediction-dot prediction-dot-user ${showingUserPredictions && i === currentUserIndex ? 'active' : ''}`}
                        title={`User Prediction ${i + 1}`}
                      />
                    ))}
                  </div>
                  {/* Navigation controls */}
                  <div className="chart-nav">
                    <button
                      type="button"
                      className="chart-nav-btn"
                      onClick={handlePrevious}
                      disabled={!canGoPrevious}
                      aria-label="Previous prediction"
                    >
                      ‹
                    </button>
                    <span className="chart-nav-indicator">
                      {showingUserPredictions
                        ? `User ${currentUserIndex + 1}/${totalUserPredictions}`
                        : `AI ${currentIndex + 1}/${totalPredictions}`}
                    </span>
                    <button
                      type="button"
                      className="chart-nav-btn"
                      onClick={handleNext}
                      disabled={!canGoNext}
                      aria-label="Next prediction"
                    >
                      ›
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Visualization */}
      <div className="chart-visual" style={{ height: `${height}px` }}>
        {showTimelineView ? (
          <TimelineView runs={completedRuns} />
        ) : displayData.userPrediction ? (
          <UserPredictionViz prediction={displayData.userPrediction} />
        ) : (
          <AIPredictionViz
            finalProbability={displayData.finalProbability}
            agentProbabilities={displayData.agentProbabilities}
          />
        )}
      </div>
    </div>
  )
}

// Visualization for AI predictions
function AIPredictionViz({
  finalProbability,
  agentProbabilities,
}: {
  finalProbability: number | null
  agentProbabilities: number[]
}) {
  // Sort agent probabilities for cleaner vertical display
  const sortedAgents = [...agentProbabilities].sort((a, b) => b - a)
  
  return (
    <div className="chart-viz-container">
      {/* Y-axis labels */}
      <div className="chart-y-axis">
        <span>100%</span>
        <span>75%</span>
        <span>50%</span>
        <span>25%</span>
        <span>0%</span>
      </div>

      {/* Main chart area */}
      <div className="chart-plot-area">
        {/* Grid lines */}
        <div className="chart-grid">
          <div className="chart-grid-line" style={{ bottom: '100%' }} />
          <div className="chart-grid-line" style={{ bottom: '75%' }} />
          <div className="chart-grid-line" style={{ bottom: '50%' }} />
          <div className="chart-grid-line" style={{ bottom: '25%' }} />
          <div className="chart-grid-line" style={{ bottom: '0%' }} />
        </div>

        {/* Agent predictions - vertical strip on the left */}
        <div className="chart-agents-strip">
          {sortedAgents.map((prob, idx) => (
            <div
              key={idx}
              className="chart-point chart-point-agent"
              style={{ bottom: `${prob}%` }}
              title={`Agent ${idx + 1}: ${prob.toFixed(1)}%`}
            >
              <span className="chart-point-label">{prob.toFixed(0)}%</span>
            </div>
          ))}
        </div>
        <div className="chart-section-label chart-label-agents">
          Agent<br />Predictions
        </div>

        {/* Final prediction - centered */}
        {finalProbability !== null && (
          <>
            <div
              className="chart-point chart-point-final"
              style={{ bottom: `${finalProbability}%` }}
              title={`Final: ${finalProbability.toFixed(1)}%`}
            >
              <span className="chart-point-label-final-large">{finalProbability.toFixed(0)}%</span>
            </div>
            <div className="chart-section-label chart-label-final">
              Final<br />Prediction
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Visualization for user predictions with confidence intervals
function UserPredictionViz({ prediction }: { prediction: UserPrediction }) {
  const probability = prediction.probability * 100
  const confLow = prediction.confidence_low * 100
  const confHigh = prediction.confidence_high * 100
  const rangeWidth = confHigh - confLow

  return (
    <div className="chart-viz-container">
      {/* Y-axis labels */}
      <div className="chart-y-axis">
        <span>100%</span>
        <span>75%</span>
        <span>50%</span>
        <span>25%</span>
        <span>0%</span>
      </div>

      {/* Main chart area */}
      <div className="chart-plot-area chart-plot-area-user">
        {/* Grid lines */}
        <div className="chart-grid">
          <div className="chart-grid-line" style={{ bottom: '100%' }} />
          <div className="chart-grid-line" style={{ bottom: '75%' }} />
          <div className="chart-grid-line" style={{ bottom: '50%' }} />
          <div className="chart-grid-line" style={{ bottom: '25%' }} />
          <div className="chart-grid-line" style={{ bottom: '0%' }} />
        </div>

        {/* Confidence interval */}
        <div className="chart-user-ci-group">
          <div
            className="chart-ci-band"
            style={{
              bottom: `${confLow}%`,
              height: `${rangeWidth}%`,
            }}
          />
          <div
            className="chart-ci-bound chart-ci-bound-lower"
            style={{ bottom: `${confLow}%` }}
            title={`Lower bound: ${confLow.toFixed(1)}%`}
          >
            <span className="chart-ci-label-always">{confLow.toFixed(0)}%</span>
          </div>
          <div
            className="chart-ci-bound chart-ci-bound-upper"
            style={{ bottom: `${confHigh}%` }}
            title={`Upper bound: ${confHigh.toFixed(1)}%`}
          >
            <span className="chart-ci-label-always">{confHigh.toFixed(0)}%</span>
          </div>
        </div>

        {/* User prediction point */}
        <div
          className="chart-point chart-point-user"
          style={{ bottom: `${probability}%` }}
          title={`Your prediction: ${probability.toFixed(1)}%`}
        >
          <span className="chart-point-label-user">{probability.toFixed(0)}%</span>
        </div>

        {/* Rationale if provided - only visible on hover */}
        {prediction.rationale && (
          <div className="chart-rationale">
            <div className="chart-rationale-label">Rationale:</div>
            <div className="chart-rationale-text">{prediction.rationale}</div>
          </div>
        )}
      </div>
    </div>
  )
}

// Timeline view showing all predictions over time
function TimelineView({ runs }: { runs: ForecastRun[] }) {
  if (runs.length === 0) return null

  // Calculate time range
  const firstTime = new Date(runs[0].created_at).getTime()
  const lastTime = new Date(runs[runs.length - 1].created_at).getTime()
  const timeRange = lastTime - firstTime || 1

  // Collect final predictions for connecting line
  const finalPredictions: Array<{ x: number; y: number; date: string; probability: number }> = []

  // Collect all data points (final predictions + agent predictions)
  const allPoints: Array<{
    x: number // 0-100% of timeline
    y: number // 0-100% probability
    isFinal: boolean
    runIndex: number
    label: string
    date: string
  }> = []

  runs.forEach((run, runIndex) => {
    const runTime = new Date(run.created_at).getTime()
    const xPos = runs.length === 1 ? 50 : ((runTime - firstTime) / timeRange) * 100
    const dateLabel = new Date(run.created_at).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    })

    // Add final prediction (large dot)
    if (run.final_result?.probability !== null && run.final_result?.probability !== undefined) {
      const probability = run.final_result.probability * 100
      allPoints.push({
        x: xPos,
        y: probability,
        isFinal: true,
        runIndex,
        label: `${probability.toFixed(0)}%`,
        date: dateLabel,
      })
      finalPredictions.push({
        x: xPos,
        y: probability,
        date: dateLabel,
        probability,
      })
    }

    // Add agent predictions (small dots)
    const agentProbs = (run.agent_predictions || [])
      .filter((a) => a.probability !== null && a.probability !== undefined)
      .map((a) => a.probability!)

    agentProbs.forEach((prob) => {
      allPoints.push({
        x: xPos,
        y: prob * 100,
        isFinal: false,
        runIndex,
        label: '',
        date: dateLabel,
      })
    })
  })

  // Generate SVG path for connecting line
  let pathD = ''
  if (finalPredictions.length > 1) {
    pathD = finalPredictions
      .map((point, idx) => {
        const command = idx === 0 ? 'M' : 'L'
        return `${command} ${point.x} ${100 - point.y}`
      })
      .join(' ')
  }

  return (
    <div className="chart-viz-container">
      {/* Y-axis labels */}
      <div className="chart-y-axis">
        <span>100%</span>
        <span>75%</span>
        <span>50%</span>
        <span>25%</span>
        <span>0%</span>
      </div>

      {/* Timeline plot area */}
      <div className="chart-plot-area chart-timeline-plot">
        {/* Grid lines */}
        <div className="chart-grid">
          <div className="chart-grid-line" style={{ bottom: '100%' }} />
          <div className="chart-grid-line" style={{ bottom: '75%' }} />
          <div className="chart-grid-line" style={{ bottom: '50%' }} />
          <div className="chart-grid-line" style={{ bottom: '25%' }} />
          <div className="chart-grid-line" style={{ bottom: '0%' }} />
        </div>

        {/* Connecting line between final predictions */}
        {pathD && (
          <svg
            className="chart-timeline-line"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d={pathD}
              stroke="#6b5b7f"
              strokeWidth="0.3"
              fill="none"
              opacity="0.6"
            />
          </svg>
        )}

        {/* Data points */}
        {allPoints.map((point, idx) => (
          <div
            key={idx}
            className={point.isFinal ? 'chart-timeline-point-final' : 'chart-timeline-point-agent'}
            style={{
              left: `${point.x}%`,
              bottom: `${point.y}%`,
            }}
            title={point.isFinal ? `${point.y.toFixed(1)}% (${point.date})` : `Agent: ${point.y.toFixed(1)}%`}
          >
            {point.isFinal && point.label && (
              <span className="chart-timeline-value-label">{point.label}</span>
            )}
          </div>
        ))}

        {/* X-axis labels - show date for each prediction */}
        <div className="chart-timeline-x-axis">
          {runs.map((run, idx) => {
            const runTime = new Date(run.created_at).getTime()
            const xPos = runs.length === 1 ? 50 : ((runTime - firstTime) / timeRange) * 100
            return (
              <span
                key={idx}
                className="chart-timeline-x-label"
                style={{
                  position: 'absolute',
                  left: `${xPos}%`,
                  transform: 'translateX(-50%)',
                }}
              >
                {new Date(run.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })}
              </span>
            )
          })}
        </div>
      </div>
    </div>
  )
}
